import { FunctionComponent, ReactNode } from 'react';

import {
  EuiBreadcrumb,
  EuiHorizontalRule,
  EuiPageTemplateProps,
  EuiText,
} from '@elastic/eui';
import { AppShell } from '@components/app-shell/AppShell';

export type Props = {
  breadcrumb?: EuiBreadcrumb[];
  alignment?: 'top' | 'center' | 'horizontalCenter';
  panelled?: boolean;
  restrictWidth?: string | number | boolean;
  pageTitle?: string;
  showSpinner?: boolean;
  sidebar?: React.ReactNode;
  sidebarWidth?: number;
  hideFlyout?: boolean;
  /**
   * Pass-throughs to the AppShell chrome so pages that stay on this adapter can
   * still drive the new TopBar action slot (inline on desktop / overflow on
   * phone) and the banner host — without migrating off the shim. Used by the
   * Landing for its period selector + Add actions.
   */
  topBarActions?: ReactNode[];
  banner?: ReactNode;
  /**
   * Retained for caller compatibility. The chrome breakpoint is now centralized
   * in `useUiChrome` (xs/s/m = overlay), which already subsumes the old
   * per-page mobile constraint, so this is accepted but no longer consumed.
   */
  customConstraint?: string;
};

/**
 * Thin adapter. Historically this was the whole app chrome (HeaderPrimary +
 * breadcrumb sub-header + sidebar flyout + announcement banners + page
 * template). It now delegates entirely to `AppShell`, so the ~28 pages
 * that render `<MainLayout>` pick up the new burger-nav chrome unchanged.
 *
 * `restrictWidth` defaults to 800 here (NOT AppShell's `false`) to preserve the
 * old MainLayout content width — zero visual shift for migrated pages.
 *
 * The dead `role` prop was removed: gating is handled by middleware + route
 * guards, never by the layout.
 */
const MainLayout: FunctionComponent<EuiPageTemplateProps & Props> = ({
  children,
  breadcrumb,
  pageTitle,
  alignment,
  panelled = false,
  restrictWidth = 800,
  showSpinner,
  sidebar,
  sidebarWidth,
  hideFlyout = false,
  topBarActions,
  banner,
}) => {
  const renderPageTitle = pageTitle ? (
    <>
      <EuiText size="xs">
        <h2>{pageTitle}</h2>
      </EuiText>
      <EuiHorizontalRule margin="m" color="lightgray" />
    </>
  ) : undefined;

  return (
    <AppShell
      breadcrumb={breadcrumb}
      pageTitle={renderPageTitle}
      alignment={alignment ?? 'top'}
      panelled={panelled}
      restrictWidth={restrictWidth}
      showSpinner={showSpinner ?? false}
      sidebar={sidebar}
      sidebarWidth={sidebarWidth}
      hideFlyout={hideFlyout}
      topBarActions={topBarActions}
      banner={banner}>
      {children}
    </AppShell>
  );
};

export default MainLayout;
